import { ChevronRightIcon } from "lucide-react";

import { EnrichmentComposer } from "./enrichment/EnrichmentComposer";
import { EnrichmentInputs } from "./enrichment/EnrichmentInputs";
import { EnrichmentMessages } from "./enrichment/EnrichmentMessages";
import { EnrichmentPortalHeader } from "./enrichment/EnrichmentPortalHeader";
import type { EnrichmentCandidacy } from "./enrichment/types";
import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { Button } from "@/components/ui/button";

import { useEnrichmentInputs } from "@/hooks/useEnrichmentInputs";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";

export const EnrichmentPortal = ({
  candidacyId,
  onApplied,
  onExit,
}: {
  candidacyId: string;
  onApplied: () => void;
  onExit: () => void;
}) => {
  /* Context */
  const { jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const {
    data: candidacyInfo,
    isPending,
    isError,
  } = useGetCandidacyInfo({ candidacyId });
  const profileQuery = useGetCandidacySheetData({ jobId, userId: workerId });

  /* React Hooks */
  const enrichmentInputs = useEnrichmentInputs();

  /* Render */
  const render = () => {
    if (isPending || profileQuery.isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    if (isError || profileQuery.isError) {
      return <ErrorText errorMessage="Error retrieving candidacy." />;
    }

    const enrichmentCandidacy: EnrichmentCandidacy = {
      candidacyId,
      profile: profileQuery.data,
      talentId: candidacyInfo.talentId ?? null,
    };

    return (
      <Column className="h-[calc(100vh-7rem)] gap-4">
        <Row className="items-center gap-1.5 text-sm">
          <Button
            className="h-auto p-0 text-slate-600 hover:bg-transparent hover:text-slate-900"
            onClick={onExit}
            variant="ghost"
          >
            Profile
          </Button>
          <ChevronRightIcon className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-primary-900">Enrich Profile</span>
        </Row>
        <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <Column className="min-h-0 flex-1 gap-4 overflow-y-auto p-5">
            <EnrichmentPortalHeader
              firstName={profileQuery.data.profile.firstName}
              lastName={profileQuery.data.profile.lastName}
            />
            <EnrichmentInputs
              candidacy={enrichmentCandidacy}
              enrichmentInputs={enrichmentInputs}
            />
            <EnrichmentMessages
              candidacy={enrichmentCandidacy}
              enrichedProfile={enrichmentInputs.enrichedProfile}
              isEnriching={enrichmentInputs.isEnriching}
              messages={enrichmentInputs.messages}
              onApplied={onApplied}
            />
          </Column>
          <div className="shrink-0 bg-slate-50 px-5 py-3">
            <EnrichmentComposer enrichmentInputs={enrichmentInputs} />
          </div>
        </div>
      </Column>
    );
  };

  return render();
};
